import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import StatCard from '../role-based-dashboard/components/StatCard';
import PredictiveChartsPanel from './components/PredictiveChartsPanel';
import TrendAnalysisWidget from './components/TrendAnalysisWidget';
import ReportBuilderPanel from './components/ReportBuilderPanel';
import AnalyticalInsightsPanel from './components/AnalyticalInsightsPanel';

const PredictiveAnalyticsCustomReporting = () => {
  const [timeHorizon, setTimeHorizon] = useState('6-months');
  const [confidenceLevel, setConfidenceLevel] = useState('95');
  const [activeView, setActiveView] = useState('predictive');

  const timeHorizonOptions = [
    { value: '3-months', label: 'Next 3 Months' },
    { value: '6-months', label: 'Next 6 Months' },
    { value: '1-year', label: 'Next Year' },
    { value: '2-years', label: 'Next 2 Years' }
  ];

  const confidenceLevelOptions = [
    { value: '90', label: '90% Confidence' },
    { value: '95', label: '95% Confidence' },
    { value: '99', label: '99% Confidence' }
  ];

  const predictiveStats = [
    {
      title: 'Predicted Placements',
      value: '1,485',
      change: '+19.2%',
      changeType: 'positive',
      icon: 'TrendingUp',
      iconColor: 'var(--color-primary)',
      trend: 'up'
    },
    {
      title: 'Hiring Demand Forecast',
      value: '2,340',
      change: '+24.5%',
      changeType: 'positive',
      icon: 'Target',
      iconColor: 'var(--color-success)',
      trend: 'up'
    },
    {
      title: 'Success Probability',
      value: '89.3%',
      change: '+3.8%',
      changeType: 'positive',
      icon: 'Award',
      iconColor: 'var(--color-accent)',
      trend: 'up'
    },
    {
      title: 'Predicted Avg. Salary',
      value: '₹8.2L',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      iconColor: 'var(--color-warning)',
      trend: 'up'
    }
  ];

  const handleGenerateForecast = () => {
    alert(`Generating forecast for ${timeHorizon} with ${confidenceLevel}% confidence level`);
  };

  const handleScheduleAnalytics = () => {
    alert('Analytics scheduled successfully!');
  };

  const handleExportPredictions = () => {
    const exportData = {
      timeHorizon,
      confidenceLevel,
      predictions: predictiveStats,
      exportedAt: new Date()?.toISOString()
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `predictive-analytics-${Date.now()}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                Predictive Analytics & Custom Reporting
              </h1>
              <p className="text-muted-foreground">
                AI-powered forecasting and customizable report generation for data-driven decisions
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" iconName="Calendar" onClick={handleScheduleAnalytics}>
                Schedule Analytics
              </Button>
              <Button variant="default" iconName="Download" onClick={handleExportPredictions}>
                Export Predictions
              </Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <Select
              options={timeHorizonOptions}
              value={timeHorizon}
              onChange={setTimeHorizon}
              className="w-full md:w-64"
              label="Forecast Horizon"
            />
            <Select
              options={confidenceLevelOptions}
              value={confidenceLevel}
              onChange={setConfidenceLevel}
              className="w-full md:w-64"
              label="Confidence Level"
            />
            <div className="flex items-end">
              <Button variant="default" iconName="Sparkles" onClick={handleGenerateForecast}>
                Generate Forecast
              </Button>
            </div>
          </div>

          <div className="border-b border-border mb-6">
            <div className="flex gap-1">
              <button
                onClick={() => setActiveView('predictive')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth border-b-2 ${
                  activeView === 'predictive' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon name="TrendingUp" size={16} />
                Predictive Dashboard
              </button>
              <button
                onClick={() => setActiveView('builder')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth border-b-2 ${
                  activeView === 'builder' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon name="LayoutGrid" size={16} />
                Report Builder
              </button>
            </div>
          </div>
        </div>

        <div className="animate-fadeIn">
          {activeView === 'predictive' ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {predictiveStats?.map((stat, index) => (
                  <StatCard key={index} {...stat} />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PredictiveChartsPanel timeHorizon={timeHorizon} confidenceLevel={confidenceLevel} />
                <TrendAnalysisWidget />
              </div>

              <AnalyticalInsightsPanel />
            </div>
          ) : (
            <ReportBuilderPanel />
          )}
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalyticsCustomReporting;